import { useCallback, useRef, useState } from 'react';
import { computeMessageBytes } from './useMessageLength';
import { messagingService } from '../api/services/messaging.service';
import type { AlimTalkSendRequest, FriendTalkSendRequest, SMSSendRequest, AlimTalkBatchSendRequest, FriendTalkBatchSendRequest, SMSBatchSendRequest, MessageBulkJob } from '../api/types/messaging.types';

export type BulkMessageType = 'AT' | 'FT' | 'SM' | 'LM';

interface BulkSendOptions {
  batchSize?: number; // max recipients per async wave
  delayMs?: number;   // optional delay between batches
  stopOnError?: boolean;
}

interface Recipient {
  id: number;
  phone_number: string;
  [key: string]: any;
}

interface BulkProgressItem {
  userId: number;
  phone: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
  message_id?: string;
}

interface StartParams {
  type: BulkMessageType;
  recipients: Recipient[];
  site_id: number;
  message: string;
  template_id?: number; // for AT
  buttons?: any[];
  fall_back_yn?: boolean;
  fall_back_message?: string;
  image_url?: string; // for FT wide image
  template_params?: Record<string, string>;
  ad_flag?: boolean;
}

export function useBulkMessaging({ batchSize = 300, delayMs = 300, stopOnError = false }: BulkSendOptions = {}) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<BulkProgressItem[]>([]);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [summary, setSummary] = useState<{ success: number; error: number; total: number }>({ success: 0, error: 0, total: 0 });
  const cancelRef = useRef(false);

  const cancel = useCallback(() => {
    cancelRef.current = true;
  }, []);

  const reset = useCallback(() => {
    setRunning(false);
    setProgress([]);
    setCurrentBatch(0);
    setTotalBatches(0);
    setCompleted(false);
    setSummary({ success: 0, error: 0, total: 0 });
    cancelRef.current = false;
  }, []);

  const start = useCallback(async (params: StartParams) => {
    if (running) return;
    reset();
    let { recipients, type } = params;
    if (!recipients || recipients.length === 0) return;

    // Auto-classify SMS/LMS if initial type is SMS and message exceeds 90 bytes.
    if (type === 'SM') {
      const bytes = computeMessageBytes(params.message || '');
      if (bytes > 90 && bytes <= 2000) {
        type = 'LM';
      }
    }

    setRunning(true);
    setSummary(s => ({ ...s, total: recipients.length }));
    setProgress(recipients.map(r => ({ userId: r.id, phone: r.phone_number, status: 'pending' as const })));

    // Try backend batch endpoints first (feature detection by simple HEAD/assume present once deployed)
    // For reliability we attempt batch only if recipients length > 1
    if (recipients.length > 1) {
      try {
        let batchResp: { data: MessageBulkJob } | null = null;
        if (type === 'AT') {
          const payload: AlimTalkBatchSendRequest = {
            site_id: params.site_id,
            template_id: params.template_id!,
            phone_numbers: recipients.map(r => r.phone_number),
            message: params.message,
            buttons: params.buttons,
            fall_back_yn: params.fall_back_yn,
            fall_back_message: params.fall_back_message,
            template_params: params.template_params
          };
          batchResp = await (messagingService as any).sendAlimTalkBatch(payload);
        } else if (type === 'FT') {
          const payload: FriendTalkBatchSendRequest = {
            site_id: params.site_id,
            phone_numbers: recipients.map(r => r.phone_number),
            message: params.message,
            buttons: params.buttons,
            ad_flag: params.ad_flag,
            image_url: params.image_url
          };
            batchResp = await (messagingService as any).sendFriendTalkBatch(payload);
        } else { // SMS/LMS
          const payload: SMSBatchSendRequest = {
            site_id: params.site_id,
            phone_numbers: recipients.map(r => r.phone_number),
            message: params.message,
            message_type: type === 'LM' ? 'LM' : 'SM'
          };
          batchResp = await (messagingService as any).sendSMSBatch(payload);
        }
        if (batchResp) {
          const job = batchResp.data;
          const results = job.result_summary?.results || [];
            setProgress(prev => prev.map(p => {
              const match = results.find(r => r.phone_number === p.phone);
              if (!match) return p;
              return {
                ...p,
                status: match.ok ? 'success' : 'error',
                error: match.error,
                message_id: match.message_id
              };
            }));
            setSummary({ success: job.success_count, error: job.failure_count, total: job.total_count });
            setCompleted(true);
            setRunning(false);
            return;
        }
      } catch (e) {
        // Fallback to client-side chunking if batch endpoint fails (404 or other)
        console.warn('Batch endpoint failed, falling back to client chunking', e);
      }
    }

    // Fallback legacy per-recipient path
    const batches: Recipient[][] = [];
    for (let i = 0; i < recipients.length; i += batchSize) {
      batches.push(recipients.slice(i, i + batchSize));
    }
    setTotalBatches(batches.length);

    let successCount = 0;
    let errorCount = 0;

    for (let b = 0; b < batches.length; b++) {
      if (cancelRef.current) break;
      setCurrentBatch(b + 1);
      const batch = batches[b];

      await Promise.all(batch.map(async (rec) => {
        if (cancelRef.current) return;
        try {
          let resp;
          if (type === 'AT') {
            const payload: AlimTalkSendRequest = {
              site_id: params.site_id,
              template_id: params.template_id!,
              phone_number: rec.phone_number,
              message: params.message,
              buttons: params.buttons,
              fall_back_yn: params.fall_back_yn,
              fall_back_message: params.fall_back_message,
              template_params: params.template_params,
            };
            resp = await messagingService.sendAlimTalk(payload);
          } else if (type === 'FT') {
            const payload: FriendTalkSendRequest = {
              site_id: params.site_id,
              phone_number: rec.phone_number,
              message: params.message,
              buttons: params.buttons,
              fall_back_yn: params.fall_back_yn,
              fall_back_message: params.fall_back_message,
              ad_flag: params.ad_flag,
            } as any;
            if (params.image_url) (payload as any).image_url = params.image_url;
            resp = await messagingService.sendFriendTalk(payload);
          } else { // SMS/LMS
            const payload: SMSSendRequest = {
              site_id: params.site_id,
              phone_number: rec.phone_number,
              message: params.message,
              message_type: type === 'LM' ? 'LM' : 'SM'
            };
            resp = await messagingService.sendSMS(payload);
          }
          successCount += 1;
          const msgId = (resp.data as any)?.message_log?.message_id || (resp.data as any)?.message_id;
          setProgress(prev => prev.map(p => p.userId === rec.id ? { ...p, status: 'success', message_id: msgId } : p));
        } catch (e: any) {
          errorCount += 1;
          let backendMsg: string | undefined;
          if (e.response?.data) {
            backendMsg = e.response.data.error || e.response.data.detail || JSON.stringify(e.response.data);
          }
          const errText = backendMsg || e.message || 'fail';
          setProgress(prev => prev.map(p => p.userId === rec.id ? { ...p, status: 'error', error: errText } : p));
          if (stopOnError) cancelRef.current = true;
        }
      }));

      setSummary({ success: successCount, error: errorCount, total: recipients.length });

      if (cancelRef.current) break;
      if (delayMs > 0 && b < batches.length - 1) {
        await new Promise(res => setTimeout(res, delayMs));
      }
    }

    setCompleted(true);
    setRunning(false);
  }, [batchSize, delayMs, running, reset, stopOnError]);

  const retryFailed = useCallback(async (originalParams: StartParams) => {
    if (running) return;
    const failedPhones = progress.filter(p => p.status === 'error').map(p => p.phone);
    if (!failedPhones.length) return;
    const failedRecipients = originalParams.recipients.filter(r => failedPhones.includes(r.phone_number));
    await start({ ...originalParams, recipients: failedRecipients });
  }, [progress, running, start]);

  return {
    start,
    cancel,
    reset,
    running,
    completed,
    progress,
    currentBatch,
    totalBatches,
    summary,
    retryFailed,
  };
}

export default useBulkMessaging;
